import { useEffect, useMemo, useState } from "react";
import { X, Trash2, ListTodo, Star, Link2 } from "lucide-react";
import { Avatar } from "@/pages/admin/saker/Avatar";
import { STRAT_STATUS, SSTATUS, LINK_TYPE_LABEL, fmtDato } from "./strategiUtils";

const Seksjon = ({ label, children }) => (
  <div className="mt-6">
    <p className="strat-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-neutral-400 mb-2">{label}</p>
    {children}
  </div>
);

const Felt = ({ label, verdi, onLagre, testid, textarea, hint }) => {
  const [v, setV] = useState(verdi ?? "");
  useEffect(() => { setV(verdi ?? ""); }, [verdi]);
  const Tag = textarea ? "textarea" : "input";
  return (
    <label className="adm-label">
      {label}
      <Tag
        value={v}
        rows={textarea ? 3 : undefined}
        onChange={(e) => setV(e.target.value)}
        onBlur={() => String(v) !== String(verdi ?? "") && onLagre(v)}
        className="adm-input"
        data-testid={testid}
      />
      {hint && <span className="block mt-1 text-[10.5px] font-normal normal-case tracking-normal text-neutral-400">{hint}</span>}
    </label>
  );
};

export default function StrategyDrawer({ card, members, linkables, onPatch, onCreateTask, onDelete, onLukk }) {
  const [sok, setSok] = useState("");
  const [nyOpt, setNyOpt] = useState("");
  const [lagerSak, setLagerSak] = useState(false);

  useEffect(() => {
    const esc = (e) => { if (e.key === "Escape") onLukk(); };
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onLukk]);

  const eier = members.find((m) => m.id === card.ownerId) || null;
  const status = SSTATUS[card.status] || SSTATUS.direction;
  const links = card.linkedEntities || [];
  const valgt = useMemo(() => new Set(links.map((l) => `${l.type}:${l.id}`)), [links]);
  const treff = useMemo(() => {
    const q = sok.trim().toLowerCase();
    if (q.length < 2) return [];
    return (linkables || [])
      .filter((l) => !valgt.has(`${l.type}:${l.id}`) && l.label.toLowerCase().includes(q))
      .slice(0, 8);
  }, [sok, linkables, valgt]);

  const visOpen = card.status === "open" || (card.options || []).length > 0 || !!card.principle;

  const leggTilOpt = () => {
    const v = nyOpt.trim();
    if (!v) return;
    onPatch({ options: [...(card.options || []), v] });
    setNyOpt("");
  };

  const lagSak = async () => {
    if (lagerSak) return;
    setLagerSak(true);
    try { await onCreateTask(); } finally { setLagerSak(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="strategy-drawer">
      <div className="sak-backdrop absolute inset-0 bg-black/25" onClick={onLukk} />
      <aside className="sak-drawer relative w-full max-w-[560px] h-full bg-white shadow-2xl border-l border-[#E7E4DB] flex flex-col">
        <div className="px-6 pt-5 pb-4 border-b border-neutral-100 shrink-0">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2.5">
                <span className="inline-flex items-center gap-1.5 text-[11px] font-medium" style={{ color: status.dot }}>
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: status.dot }} />
                  {status.label}
                </span>
                {card.pinned && (
                  <span className="strat-mono inline-flex items-center gap-1 text-[9.5px] uppercase tracking-[0.14em] text-neutral-400">
                    <Star size={10} /> North star
                  </span>
                )}
              </div>
              <h2 className="text-[19px] font-semibold text-neutral-900 leading-tight mt-1.5" data-testid="strategy-drawer-tittel">{card.title}</h2>
              <p className="text-[10.5px] text-neutral-400 mt-1">Sist endret {fmtDato(card.updatedAt)}</p>
            </div>
            <button type="button" onClick={onLukk} className="text-neutral-400 hover:text-neutral-800 transition-colors shrink-0" data-testid="strategy-drawer-lukk">
              <X size={16} />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 pb-6">
          <Seksjon label="Status">
            <div className="flex flex-wrap gap-1.5">
              {STRAT_STATUS.map((s) => (
                <button
                  key={s.k}
                  type="button"
                  onClick={() => s.k !== card.status && onPatch({ status: s.k })}
                  className={`inline-flex items-center gap-1.5 text-[11.5px] font-medium px-2.5 py-1.5 rounded-md border transition-colors ${
                    s.k === card.status ? "border-neutral-400 bg-white" : "border-[#E7E4DB] text-neutral-400 hover:border-neutral-300"
                  }`}
                  data-testid={`strategy-status-${s.k}`}
                >
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: s.dot }} />
                  {s.label}
                </button>
              ))}
            </div>
          </Seksjon>

          <Seksjon label="Kort">
            <div className="grid gap-3">
              <Felt label="Tittel" verdi={card.title} onLagre={(v) => v.trim() && onPatch({ title: v })} testid="strategy-felt-tittel" />
              <Felt label="Statement" verdi={card.statement} onLagre={(v) => onPatch({ statement: v })} testid="strategy-felt-statement" textarea hint="1–3 setninger. Kortet skal kunne leses på fem sekunder." />
              <label className="adm-label">
                Eier
                <select
                  value={card.ownerId || ""}
                  onChange={(e) => onPatch({ ownerId: e.target.value || null })}
                  className="adm-input"
                  data-testid="strategy-felt-eier"
                >
                  <option value="">Ingen eier</option>
                  {members.map((m) => (
                    <option key={m.id} value={m.id}>{m.name}</option>
                  ))}
                </select>
                {members.length === 0 && (
                  <span className="block mt-1 text-[10.5px] font-normal normal-case tracking-normal text-neutral-400">Legg til personer under Saker → Personer.</span>
                )}
              </label>
              {eier && (
                <div className="flex items-center gap-2 text-[12px] text-neutral-600">
                  <Avatar person={eier} size={20} /> {eier.name}
                </div>
              )}
            </div>
          </Seksjon>

          {visOpen && (
            <Seksjon label="Åpen beslutning">
              <div className="flex flex-wrap gap-1.5 mb-2">
                {(card.options || []).map((o) => (
                  <span key={o} className="strat-mono inline-flex items-center gap-1.5 text-[10.5px] border border-neutral-200 rounded px-2 py-1 text-neutral-600" data-testid={`strategy-opt-${o}`}>
                    {o}
                    <button type="button" onClick={() => onPatch({ options: (card.options || []).filter((x) => x !== o) })} className="text-neutral-300 hover:text-neutral-700" aria-label={`Fjern ${o}`}>
                      <X size={10} />
                    </button>
                  </span>
                ))}
              </div>
              <input
                value={nyOpt}
                onChange={(e) => setNyOpt(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); leggTilOpt(); } }}
                className="adm-input"
                placeholder="Legg til alternativ — Enter"
                data-testid="strategy-opt-input"
              />
              <div className="mt-3">
                <Felt label="Decision principle" verdi={card.principle} onLagre={(v) => onPatch({ principle: v })} testid="strategy-felt-principle" textarea hint="Prinsippet som avgjør valget — ikke selve diskusjonen." />
              </div>
            </Seksjon>
          )}

          <Seksjon label="Koblinger">
            {links.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-2">
                {links.map((l) => (
                  <span key={`${l.type}:${l.id}`} className="inline-flex items-center gap-1.5 text-[11px] border border-neutral-200 rounded px-2 py-1 text-neutral-600" data-testid={`strategy-link-${l.id}`}>
                    <span className="strat-mono text-[9px] uppercase tracking-[0.1em] text-neutral-400">{LINK_TYPE_LABEL[l.type] || l.type}</span>
                    {l.label}
                    <button
                      type="button"
                      onClick={() => onPatch({ linkedEntities: links.filter((x) => !(x.type === l.type && x.id === l.id)) })}
                      className="text-neutral-300 hover:text-neutral-700"
                      aria-label={`Fjern ${l.label}`}
                    >
                      <X size={10} />
                    </button>
                  </span>
                ))}
              </div>
            )}
            <input
              value={sok}
              onChange={(e) => setSok(e.target.value)}
              className="adm-input"
              placeholder="Søk speakers, targets, partnere, sessions, experiences …"
              data-testid="strategy-link-sok"
            />
            {treff.length > 0 && (
              <div className="mt-1.5 border border-[#E7E4DB] rounded-lg overflow-hidden divide-y divide-neutral-100">
                {treff.map((l) => (
                  <button
                    key={`${l.type}:${l.id}`}
                    type="button"
                    onClick={() => { onPatch({ linkedEntities: [...links, l] }); setSok(""); }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-left text-[12px] text-neutral-700 hover:bg-neutral-50 transition-colors"
                    data-testid={`strategy-link-treff-${l.id}`}
                  >
                    <span className="strat-mono text-[9px] uppercase tracking-[0.1em] text-neutral-400 w-[72px] shrink-0">{LINK_TYPE_LABEL[l.type] || l.type}</span>
                    <span className="truncate">{l.label}</span>
                  </button>
                ))}
              </div>
            )}
          </Seksjon>

          <Seksjon label="Notat">
            <Felt label="" verdi={card.note} onLagre={(v) => onPatch({ note: v })} testid="strategy-felt-notat" textarea hint="Valgfritt — kort kontekst, ikke en logg." />
          </Seksjon>
        </div>

        <div className="px-6 py-4 border-t border-neutral-100 shrink-0 flex items-center gap-2">
          <button type="button" onClick={lagSak} disabled={lagerSak} className="adm-btn-ghost inline-flex items-center gap-1.5" data-testid="strategy-create-task-btn">
            <ListTodo size={13} /> {lagerSak ? "Oppretter …" : "Opprett sak"}
          </button>
          {card.openTasks > 0 && <span className="text-[11px] text-neutral-400">{card.openTasks} åpne saker</span>}
          <button
            type="button"
            onClick={() => onPatch({ pinned: !card.pinned })}
            className="adm-btn-ghost inline-flex items-center gap-1.5 ml-auto"
            data-testid="strategy-pin-btn"
            title={card.pinned ? "Fjern som north star" : "Sett som north star øverst på boardet"}
          >
            <Star size={13} /> {card.pinned ? "Fjern north star" : "North star"}
          </button>
          <button
            type="button"
            onClick={() => { if (window.confirm(`Slette kortet «${card.title}»?`)) onDelete(); }}
            className="adm-btn-danger inline-flex items-center gap-1.5"
            data-testid="strategy-slett-btn"
          >
            <Trash2 size={13} /> Slett
          </button>
        </div>
      </aside>
    </div>
  );
}
